/**
 * 他の画面・端末でのアクティビティ変更を SSE で受けて表示を取り直す
 *
 * Visit の編集・作成・統合・削除や移動手段の変更があると、バックエンドが
 * `activity.changed`（対象日付つき）を配信する。表示中の日付が含まれていれば、
 * 日別タイムラインのストア・日別データ（無限タイムライン／カレンダー）・レシートを取り直す。
 * 自分の画面で行った変更は楽観的に反映済みなので無視する。
 */

import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useRef } from 'react'

import { CLIENT_ID } from '@/api/client'
import type { SyncEvent } from '@/api/sync-events'
import { useSyncEventListener } from '@/hooks/useSyncEventListener'
import { receiptKeys } from '@/pages/receipts/hooks/use-receipts'
import { useOverlandStore } from '@/store/useOverlandStore'

import { DAY_DATA_QUERY_PREFIX } from './day-data'

/** 一括削除などで続けて届くイベントをまとめて1回の取り直しにする */
const FLUSH_DELAY_MS = 300

type Pending = { all: boolean; dates: Set<string> }

/**
 * @param storeDate 日別タイムラインがストアに読み込んでいる日付。ストアを使っていない表示なら null
 */
export const useActivityRemoteSync = (storeDate: string | null): void => {
  const queryClient = useQueryClient()
  const storeDateRef = useRef(storeDate)
  useEffect(() => {
    storeDateRef.current = storeDate
  }, [storeDate])

  const pendingRef = useRef<Pending>({ all: false, dates: new Set() })
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [])

  const flush = useCallback(async () => {
    timerRef.current = null
    const { all, dates } = pendingRef.current
    pendingRef.current = { all: false, dates: new Set() }

    void queryClient.invalidateQueries({ queryKey: receiptKeys.all })
    if (all) {
      void queryClient.invalidateQueries({ queryKey: [DAY_DATA_QUERY_PREFIX] })
    } else {
      for (const date of dates) {
        void queryClient.invalidateQueries({ queryKey: [DAY_DATA_QUERY_PREFIX, date] })
      }
    }

    const date = storeDateRef.current
    if (!date || (!all && !dates.has(date))) return
    // 移動セッションは Visit で区切るので、Visit を先に取り直す
    const { fetchVisits, fetchLocations } = useOverlandStore.getState()
    await fetchVisits(date, undefined, { silent: true })
    // 取り直している間に日付が切り替わったら、新しい日付の読み込みに任せる
    if (storeDateRef.current !== date) return
    await fetchLocations(date, undefined, { silent: true })
  }, [queryClient])

  const handleEvent = useCallback(
    (event: SyncEvent) => {
      if (event.type !== 'activity.changed') return
      if (event.origin === CLIENT_ID) return

      const pending = pendingRef.current
      if (event.dates) {
        event.dates.forEach((date) => pending.dates.add(date))
      } else {
        pending.all = true
      }
      if (timerRef.current) return
      timerRef.current = setTimeout(() => void flush(), FLUSH_DELAY_MS)
    },
    [flush],
  )
  useSyncEventListener(handleEvent)
}
